/**
 * MSAL instance + token acquisition helpers.
 *
 * Ported from apps/frontend/src-legacy/auth/msal.ts. This is the single
 * source of truth for the MSAL PublicClientApplication used by both the
 * app shell (main.jsx) and the API client (api/client.js), so the client
 * can silently acquire access tokens without duplicating MSAL config.
 */

import { EventType, PublicClientApplication } from "@azure/msal-browser";
import { buildAuthorityUrl, entraConfig, apiTokenRequest } from "./config.js";

const authority = buildAuthorityUrl(entraConfig.tenantId);
const appOrigin = window.location.origin;

const msalConfig = {
  auth: {
    clientId: entraConfig.clientId,
    authority,
    redirectUri: `${appOrigin}${entraConfig.redirectPath}`,
    postLogoutRedirectUri: `${appOrigin}${entraConfig.postLogoutRedirectPath}`
  },
  cache: {
    cacheLocation: "localStorage"
  }
};

export const loginRequest = {
  scopes: ["openid", "profile", "email", "User.Read"]
};

export const msalInstance = new PublicClientApplication(msalConfig);

msalInstance.addEventCallback((event) => {
  if (event.eventType !== EventType.LOGIN_SUCCESS) return;
  const result = event.payload;
  if (result?.account) {
    msalInstance.setActiveAccount(result.account);
  }
});

export async function initializeMsal() {
  await msalInstance.initialize();

  const activeAccount = msalInstance.getActiveAccount();
  if (activeAccount) return;

  const allAccounts = msalInstance.getAllAccounts();
  if (allAccounts.length > 0) {
    msalInstance.setActiveAccount(allAccounts[0]);
  }
}

/**
 * The signed-in account, adopting one from the account list when MSAL has an
 * account but has not promoted it to "active" yet.
 *
 * Three places set the active account — the LOGIN_SUCCESS callback,
 * initializeMsal(), and LoginGate's effect — and there is a window in which
 * none of them has run but MSAL already holds the account:
 * useIsAuthenticated() flips true as soon as accounts.length > 0, so LoginGate
 * renders the app in the SAME commit as the effect that would promote it, and
 * React runs child effects before parent effects. The app's bootstrap()
 * therefore ran first and saw no active account.
 *
 * That surfaced as "We couldn't sign you in — No active MSAL session" on load,
 * with Retry succeeding because the effect had run by then. Reading the account
 * list closes the window for good: reporting no session while MSAL demonstrably
 * holds an account is never the right answer.
 *
 * @returns {import('@azure/msal-browser').AccountInfo | null}
 */
export function resolveAccount() {
  const active = msalInstance.getActiveAccount();
  if (active) return active;

  const all = msalInstance.getAllAccounts();
  if (all.length === 0) return null;

  // Adopt it so every later caller agrees on the same account.
  msalInstance.setActiveAccount(all[0]);
  return all[0];
}

/**
 * Acquire a Bearer token for the Omni Cloud API, silently where possible,
 * falling back to an interactive redirect when a refresh/consent is needed.
 * Used by api/client.js on every request.
 *
 * @returns {Promise<string|null>} the access token, or null if there is no
 *   signed-in account (caller should treat this as unauthenticated).
 */
/**
 * A BLANK page for the silent-renew iframe to land on.
 *
 * The app's own redirectUri is `/login`, which is a real SPA route. When a
 * refresh token expires, MSAL renews in a hidden iframe pointed at the
 * redirectUri — so the iframe loaded `/login`, the service worker's
 * navigateFallback served index.html, the WHOLE APP booted inside the iframe,
 * and MSAL failed with `block_iframe_reload` followed by `timed_out`. The
 * client then had no token, every call 401'd, and the once-a-minute
 * interactive-redirect guard masked the recovery — so the app looked signed out
 * with no way back except a manual retry.
 *
 * `public/auth-redirect.html` is an empty document that exists for exactly this
 * and was never wired up. Both layers already exempt it from being rewritten to
 * index.html — `staticwebapp.config.json` excludes `/*.html` from
 * navigationFallback, and the PWA config lists it in navigateFallbackDenylist —
 * so the iframe gets a genuinely blank page and cannot re-enter the app.
 *
 * Applied to the SILENT request only. Interactive sign-in keeps landing on
 * `/login`, because a blank page has no MSAL to complete that exchange and the
 * user would be stranded on it. Registered as a SPA redirect URI for both the
 * azurestaticapps.net origin and omni.ballyvesey.com (2026-09-08), and derived
 * from window.location.origin so whichever host is in use resolves correctly.
 */
const SILENT_REDIRECT_URI = `${appOrigin}/auth-redirect.html`;

export async function acquireApiToken() {
  const account = resolveAccount();
  if (!account) return null;

  try {
    const result = await msalInstance.acquireTokenSilent({
      ...apiTokenRequest,
      account,
      redirectUri: SILENT_REDIRECT_URI
    });
    return result.accessToken;
  } catch (error) {
    // access_as_user is exposed + admin-consented on the app registration
    // (2026-07-07), so silent acquisition should succeed for signed-in
    // accounts. On failure return null — the request will 401 and the
    // throttled redirectToLogin() handles re-auth. NEVER redirect from
    // inside a request (infinite login loop).
    console.error("API token acquisition failed:", error instanceof Error ? error.message : error);
    return null;
  }
}

const LOGIN_REDIRECT_GUARD_KEY = "omni:lastLoginRedirectAt";
const LOGIN_REDIRECT_MIN_INTERVAL_MS = 60_000;

/**
 * Forces an interactive re-login, used on API 401s (replaces the beta's
 * /auth/logout redirect). Throttled to at most one interactive redirect per
 * minute so a persistently-401ing API surfaces errors instead of looping
 * the page.
 */
export async function redirectToLogin() {
  const now = Date.now();
  const last = Number(sessionStorage.getItem(LOGIN_REDIRECT_GUARD_KEY) ?? "0");
  if (Number.isFinite(last) && now - last < LOGIN_REDIRECT_MIN_INTERVAL_MS) {
    console.warn("Suppressing repeated login redirect (guard active); API is returning 401s.");
    return;
  }
  sessionStorage.setItem(LOGIN_REDIRECT_GUARD_KEY, String(now));
  await msalInstance.loginRedirect(loginRequest);
}

export async function signOut() {
  const account = msalInstance.getActiveAccount();
  await msalInstance.logoutRedirect({ account });
}
